import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Spinner } from '@atoms/Spinner';
import { Modal } from '@atoms/Modal';
import { BarcodeScanner } from '@molecules/BarcodeScanner';
import { ProductBarcodeCapture } from '@molecules/ProductBarcodeCapture';
import { catalogService } from '@services/catalogService';
import { storageService } from '@services/storageService';
import { formatCurrency } from '@utils/formatters';
import { getErrorMessage } from '@services/api';
import { useLightboxStore } from '@store/uiStore';
import type { Product } from '@/types';

const emptyForm = {
  name: '',
  price: '',
  unit: 'Adet',
  category: '',
  barcode: '',
  image: '',
};

function applyProductToForm(product: Product) {
  return {
    name: product.name,
    price: String(product.price),
    unit: product.unit,
    category: product.category,
    barcode: product.barcode ?? '',
    image: product.image ?? '',
  };
}

export function ProductsSection() {
  const queryClient = useQueryClient();
  const openLightbox = useLightboxStore((s) => s.openLightbox);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileRef = useRef<HTMLInputElement>(null);
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanBarcode, setScanBarcode] = useState('');
  const [scanResult, setScanResult] = useState<Product | null>(null);
  const [scanError, setScanError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const { data: products, isLoading } = useQuery({
    queryKey: ['wholesaler', 'products'],
    queryFn: () => catalogService.getMyProducts(),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => catalogService.getCategories(),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      catalogService.createProduct({
        name: form.name,
        price: Number(form.price),
        unit: form.unit,
        category: form.category,
        barcode: form.barcode || undefined,
        image: form.image || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'products'] });
      setShowForm(false);
      setForm(emptyForm);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      catalogService.updateProduct(editProduct!.id, {
        name: editForm.name,
        price: Number(editForm.price),
        unit: editForm.unit,
        category: editForm.category,
        barcode: editForm.barcode || undefined,
        image: editForm.image || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'products'] });
      setEditProduct(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => catalogService.deleteProduct(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wholesaler', 'products'] }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      catalogService.updateProduct(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wholesaler', 'products'] }),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, target }: { file: File; target: 'create' | 'edit' }) =>
      storageService.uploadImage(file).then((url) => ({ url, target })),
    onSuccess: ({ url, target }) => {
      if (target === 'create') setForm((prev) => ({ ...prev, image: url }));
      else setEditForm((prev) => ({ ...prev, image: url }));
    },
  });

  const handleSearchScan = async (code?: string) => {
    const value = code ?? scanBarcode.trim();
    setScanError('');
    setScanResult(null);
    if (!value) return;
    setScanBarcode(value);
    try {
      const product = await catalogService.scanBarcode(value);
      setScanResult(product);
    } catch (error) {
      setScanError(getErrorMessage(error, 'Ürün bulunamadı'));
    }
  };

  const openEdit = (p: Product) => {
    setEditProduct(p);
    setEditForm({
      name: p.name,
      price: String(p.price),
      unit: p.unit,
      category: p.category,
      barcode: p.barcode ?? '',
      image: p.image ?? '',
    });
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Ürünler</h2>
        <Button
          size="sm"
          onClick={() => {
            if (showForm) {
              setShowForm(false);
              setForm(emptyForm);
            } else {
              setShowForm(true);
            }
          }}
        >
          {showForm ? 'İptal' : 'Yeni Ürün'}
        </Button>
      </div>

      <Card className="grid gap-3 md:grid-cols-[1fr_auto_auto]">
        <Input
          label="Barkod ile ara"
          value={scanBarcode}
          onChange={(e) => setScanBarcode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSearchScan();
          }}
          placeholder="8690000000000"
        />
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={() => void handleSearchScan()}>Ara</Button>
          <Button variant="outline" onClick={() => setShowScanner(true)}>Kamera</Button>
        </div>
        {scanResult && (
          <p className="text-sm text-green-700 md:col-span-3">
            Bulundu: {scanResult.name} — {formatCurrency(Number(scanResult.price))}
            <Button size="sm" variant="outline" className="ml-2" onClick={() => openEdit(scanResult)}>
              Düzenle
            </Button>
          </p>
        )}
        {scanError && <p className="text-sm text-red-600 md:col-span-3">{scanError}</p>}
      </Card>

      {showForm && (
        <Card className="grid gap-4">
          <ProductBarcodeCapture
            barcode={form.barcode}
            onBarcodeChange={(barcode) => setForm((prev) => ({ ...prev, barcode }))}
            onProductFound={(product) => setForm((prev) => ({ ...prev, ...applyProductToForm(product) }))}
          />

          <div className="grid gap-3 md:grid-cols-2">
            <Input label="Ürün Adı" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input label="Fiyat" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <Input label="Birim" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Kategori</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="">Seçin</option>
                {(categories ?? []).map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </label>
            <div>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMutation.mutate({ file: f, target: 'create' }); }} />
              <Button type="button" variant="outline" loading={uploadMutation.isPending} onClick={() => fileInputRef.current?.click()}>Görsel Yükle</Button>
              {form.image && <img src={form.image} alt="" className="mt-2 h-20 w-20 rounded-lg object-cover" />}
            </div>
            <div className="flex items-end">
              <Button loading={createMutation.isPending} disabled={!form.name || !form.price || !form.category} onClick={() => createMutation.mutate()}>Kaydet</Button>
            </div>
          </div>
        </Card>
      )}

      <Modal open={Boolean(editProduct)} onClose={() => setEditProduct(null)} title="Ürün Düzenle">
        <div className="grid gap-4">
          <ProductBarcodeCapture
            barcode={editForm.barcode}
            onBarcodeChange={(barcode) => setEditForm((prev) => ({ ...prev, barcode }))}
            onProductFound={(product) => setEditForm((prev) => ({ ...prev, ...applyProductToForm(product) }))}
          />

          <Input label="Ürün Adı" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          <Input label="Fiyat" type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
          <Input label="Birim" value={editForm.unit} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })} />
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Kategori</span>
            <select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2">
              <option value="">Seçin</option>
              {(categories ?? []).map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </label>
          <div>
            <input ref={editFileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMutation.mutate({ file: f, target: 'edit' }); }} />
            <Button type="button" variant="outline" onClick={() => editFileRef.current?.click()}>Görsel Yükle</Button>
            {editForm.image && <img src={editForm.image} alt="" className="mt-2 h-20 w-20 rounded-lg object-cover" />}
          </div>
          <Button loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>Güncelle</Button>
        </div>
      </Modal>

      <BarcodeScanner
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={(code) => void handleSearchScan(code)}
        title="Barkod / QR ile Ara"
      />

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {(products ?? []).map((p: Product) => (
          <Card key={p.id}>
            {p.image && (
              <button type="button" onClick={() => openLightbox(p.image!, p.name)}>
                <img src={p.image} alt={p.name} className="mb-2 h-32 w-full rounded-lg object-cover" />
              </button>
            )}
            <h3 className="font-medium">{p.name}</h3>
            <p className="text-brand-600">{formatCurrency(Number(p.price))} / {p.unit}</p>
            <p className="text-xs text-gray-500">{p.category}</p>
            {p.barcode && <p className="text-xs text-gray-400">Barkod: {p.barcode}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(p)}>Düzenle</Button>
              <Button size="sm" variant="outline" onClick={() => toggleMutation.mutate({ id: p.id, isActive: !p.isActive })}>
                {p.isActive ? 'Pasifleştir' : 'Aktifleştir'}
              </Button>
              <Button size="sm" variant="danger" onClick={() => deleteMutation.mutate(p.id)}>Sil</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
